import { parsePath, useLocation } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import type { Entity } from "@common/types/entity";
import { ConsolidatedAvatar } from "@components/avatar/ConsolidatedAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useConsolidationReportContext } from "@components/accounting/consolidationReports/useConsolidationReportContext";
import { useReportConsolidationGroup } from "@components/accounting/useReportConsolidationGroup";
import {
    buildReportingConsolidationRoute,
    buildReportingEntityRoute,
} from "@components/reporting/reportingRoutes";
import { isEligibleReportingEntity } from "@components/reporting/reportingScope";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { useSideNavMode } from "@design-system/SideNav/SideNavProvider";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import { useSideNavMemory } from "@design-system/SideNav/useSideNavMemory";
import { useEntities } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { getConsolidationGroupNavBookmarks } from "../consolidationGroupNavBookmarks";
import { isOnSubsectionPath } from "../isOnSubsectionPath";
import { isCatalogLinkActive } from "./isCatalogLinkActive";
import { ACCOUNTING_REPORTS_PATH } from "./workspaceSideNavSections";

const PROFIT_LOSS_PATH = "/accounting/profit-loss";
const BALANCE_SHEET_PATH = "/accounting/balance-sheet";

const useIsOnPath = () => {
    const { pathname } = useLocation();
    return (path: string) => isOnSubsectionPath(pathname, [path]);
};

const useIsCatalogLinkActive = () => {
    const { pathname, search } = useLocation();
    return (to: string) => isCatalogLinkActive({ to, pathname, search });
};

/** The entity's report catalog: `/reporting` with consolidation, else `/accounting`. */
const useEntityReportsPath = (entity: Entity) => {
    const { consolidation: isReportingEnabled } = useTypedFlags();
    if (!isReportingEnabled) {
        return `${ACCOUNTING_REPORTS_PATH}?entityId=${entity.id}`;
    }
    return isEligibleReportingEntity(entity)
        ? buildReportingEntityRoute(entity.id)
        : undefined;
};

/**
 * The consolidation group's nav reports (its bookmarks, then its catalog) and
 * which is the page. Shared by the open sub-rows and the mini row so both
 * light up on the same pages. Null until the group's context is ready.
 */
const useConsolidationGroupNavReports = () => {
    const result = useConsolidationReportContext();
    const [consolidationGroupId] = useQueryParam(
        "consolidationGroupId",
        StringParam,
    );
    const isOnPath = useIsOnPath();
    const isCatalogActive = useIsCatalogLinkActive();

    if (result.status !== "ready") {
        return null;
    }
    const { group } = result.context;
    const isGroupInScope = consolidationGroupId === group.id;
    const catalogPath = buildReportingConsolidationRoute(group.id);
    const bookmarks = getConsolidationGroupNavBookmarks({
        context: result.context,
    }).map(({ id, url, label }) => ({
        id,
        url,
        label,
        isActive: isGroupInScope && isOnPath(parsePath(url).pathname ?? ""),
    }));
    const isCatalogPageActive = isCatalogActive(catalogPath);

    return {
        bookmarks,
        catalogPath,
        isCatalogActive: isCatalogPageActive,
        isAnyActive:
            isCatalogPageActive ||
            bookmarks.some((bookmark) => bookmark.isActive),
    };
};

const ConsolidationGroupBookmarks = () => {
    const reports = useConsolidationGroupNavReports();

    if (reports === null) {
        return null;
    }

    return (
        <>
            {reports.bookmarks.map((bookmark) => (
                <SideNavSubItem
                    key={bookmark.id}
                    to={bookmark.url}
                    isActive={bookmark.isActive}
                    label={bookmark.label}
                />
            ))}
            <SideNavSubItem
                to={reports.catalogPath}
                isActive={reports.isCatalogActive}
                label="All reports"
            />
        </>
    );
};

/** The workspace's consolidation group, expanded by default into its reports. */
export const ConsolidationGroupRow = () => {
    const entities = useEntities();
    const { consolidationGroup } = useReportConsolidationGroup();
    const [isExpanded, setIsExpanded] = useSideNavMemory({
        id: "reports:consolidation-group",
        initialValue: true,
    });
    const reports = useConsolidationGroupNavReports();
    const isMini = useSideNavMode() === "mini";
    const parentEntity = entities.find(
        (entity) => entity.uuid === consolidationGroup?.parentEntityUuid,
    );

    if (!consolidationGroup || !parentEntity) {
        return null;
    }
    const parentName = parentEntity.name ?? "Group";
    const rowTarget = isMini
        ? {
              to: buildReportingConsolidationRoute(consolidationGroup.id),
              isActive: reports?.isAnyActive ?? false,
          }
        : { onClick: () => setIsExpanded(!isExpanded) };

    return (
        <SideNavItem
            {...rowTarget}
            icon={
                <ConsolidatedAvatar name={parentName} size="xs" aria-hidden />
            }
            label={`${parentName} (Consolidation Group)`}
            isExpanded={isExpanded}
            onExpandedChange={setIsExpanded}
        >
            <ConsolidationGroupBookmarks />
        </SideNavItem>
    );
};

/**
 * A business entity that expands into its Profit & Loss, Balance Sheet and
 * reports. Several can be open at once; each keeps its own expansion.
 */
export const EntityReportsRow = ({
    entity,
    isExpandedByDefault,
}: {
    entity: Entity;
    isExpandedByDefault: boolean;
}) => {
    const [isExpanded, setIsExpanded] = useSideNavMemory({
        id: `reports:entity:${entity.id}`,
        initialValue: isExpandedByDefault,
    });
    const [entityId] = useQueryParam("entityId", NumberParam);
    const [consolidationGroupId] = useQueryParam(
        "consolidationGroupId",
        StringParam,
    );
    const isOnPath = useIsOnPath();
    const isCatalogActive = useIsCatalogLinkActive();
    const entityReportsPath = useEntityReportsPath(entity);
    const isEntityInScope =
        consolidationGroupId === undefined && entityId === entity.id;
    const entityQuery = `?entityId=${entity.id}`;
    const isProfitLossActive = isEntityInScope && isOnPath(PROFIT_LOSS_PATH);
    const isBalanceSheetActive =
        isEntityInScope && isOnPath(BALANCE_SHEET_PATH);
    const isAllReportsActive =
        entityReportsPath !== undefined && isCatalogActive(entityReportsPath);
    const isMini = useSideNavMode() === "mini";
    const rowTarget = isMini
        ? {
              to: entityReportsPath ?? `${PROFIT_LOSS_PATH}${entityQuery}`,
              isActive:
                  isProfitLossActive ||
                  isBalanceSheetActive ||
                  isAllReportsActive,
          }
        : { onClick: () => setIsExpanded(!isExpanded) };

    return (
        <SideNavItem
            {...rowTarget}
            icon={
                <EntityAvatar
                    entity={entity}
                    size="xs"
                    copyId={entity.id}
                    aria-hidden
                />
            }
            label={entity.name ?? ""}
            labelCopyId={entity.id}
            labelCopyLabel="Entity"
            isExpanded={isExpanded}
            onExpandedChange={setIsExpanded}
        >
            <SideNavSubItem
                to={`${PROFIT_LOSS_PATH}${entityQuery}`}
                isActive={isProfitLossActive}
                label="Profit & Loss"
            />
            <SideNavSubItem
                to={`${BALANCE_SHEET_PATH}${entityQuery}`}
                isActive={isBalanceSheetActive}
                label="Balance Sheet"
            />
            {entityReportsPath !== undefined && (
                <SideNavSubItem
                    to={entityReportsPath}
                    isActive={isAllReportsActive}
                    label="All reports"
                />
            )}
        </SideNavItem>
    );
};
